import { ModerationStatus as ModerationStatusEnum } from "@next-platform/packages-ts-types";
import { CHARACTER_MODERATION_STATUS, CharacterType, useTranslatedModerationReason } from "@next-platform/client-common-core";
import { Spacer } from "@nextui-org/react";
import { useMemo } from "react";

import TextStyles from "@/components/common/TextStyles";
import { Col, Container, Grid, Row, Text } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION } from "@/constants";

type DataCategory = "chatbot" | "lorebook";

interface IModerationStatusProps {
  applicationId: string;
  moderationReasonCodes?: string[];
  actionTakenDescription?: string;
  reasonForModeration?: string[];
  moderationStatus?: CHARACTER_MODERATION_STATUS | ModerationStatusEnum | null;
  reportsType?: string[];
  type?: CharacterType;
  category?: DataCategory;
}

interface IRenderModerationDetails {
  characterType: CharacterType | undefined;
  isReportTypeUser: boolean;
  moderationStatus: CHARACTER_MODERATION_STATUS | ModerationStatusEnum;
  category: DataCategory;
}

interface IRenderReviewModerationDetails {
  category: DataCategory;
  moderationStatus: CHARACTER_MODERATION_STATUS | ModerationStatusEnum;
}

const renderFixRequiredTitle = ({ category, characterType, isReportTypeUser }: Omit<IRenderModerationDetails, "moderationStatus">) => {
  if (characterType === CharacterType.META) {
    if (isReportTypeUser) {
      return category === "chatbot" ? (
        <Trans i18nKey={"chatbot:moderation.actionRequired.groupReported"} />
      ) : (
        <Trans i18nKey={"lorebook:moderation.actionRequired.groupReported"} />
      );
    }

    return category === "chatbot" ? (
      <Trans i18nKey={"chatbot:moderation.actionRequired.groupRejected"} />
    ) : (
      <Trans i18nKey={"lorebook:moderation.actionRequired.groupRejected"} />
    );
  }

  if (isReportTypeUser) {
    return category === "chatbot" ? (
      <Trans i18nKey={"chatbot:moderation.actionRequired.chatbotReported"} />
    ) : (
      <Trans i18nKey={"lorebook:moderation.actionRequired.lorebookReported"} />
    );
  }

  return category === "chatbot" ? (
    <Trans i18nKey={"chatbot:moderation.actionRequired.chatbotRejected"} />
  ) : (
    <Trans i18nKey={"lorebook:moderation.actionRequired.lorebookRejected"} />
  );
};

const renderReviewModerationTitle = ({ category, moderationStatus }: IRenderReviewModerationDetails) => {
  switch (moderationStatus) {
    case CHARACTER_MODERATION_STATUS.PENDING:
    case CHARACTER_MODERATION_STATUS.REREVIEW_REQUIRED:
      return category === "chatbot" ? <Trans i18nKey="chatbot:moderation.underReview" /> : <Trans i18nKey="lorebook:moderation.underReview" />;

    case CHARACTER_MODERATION_STATUS.MANUAL_REVIEW_REQUIRED:
      return category === "chatbot" ? (
        <Trans i18nKey="chatbot:moderation.underManualReview" />
      ) : (
        <Trans i18nKey="lorebook:moderation.underManualReview" />
      );

    default:
      return null;
  }
};

const renderModerationTitle = ({ category, characterType, isReportTypeUser, moderationStatus }: IRenderModerationDetails) => {
  if (moderationStatus === CHARACTER_MODERATION_STATUS.FIX_REQUIRED) {
    return renderFixRequiredTitle({ category, characterType, isReportTypeUser });
  }

  return renderReviewModerationTitle({ category, moderationStatus });
};

const renderModerationDescription = ({ category, characterType, isReportTypeUser, moderationStatus }: IRenderModerationDetails) => {
  let entityType = { entityType: characterType === CharacterType.META ? "group" : "chatbot" };
  if (category === "lorebook") {
    entityType = { entityType: "lorebook" };
  }
  if (moderationStatus === CHARACTER_MODERATION_STATUS.FIX_REQUIRED) {
    if (isReportTypeUser) {
      return category === "chatbot" ? (
        <Trans i18nKey={"chatbot:moderation.fixRequired.unpublished"} values={entityType} />
      ) : (
        <Trans i18nKey={"lorebook:moderation.fixRequired.unpublished"} values={entityType} />
      );
    }
    return category === "chatbot" ? (
      <Trans i18nKey={"chatbot:moderation.fixRequired.rejected"} values={entityType} />
    ) : (
      <Trans i18nKey={"lorebook:moderation.fixRequired.rejected"} values={entityType} />
    );
  }
  return category === "chatbot" ? (
    <Trans i18nKey={"chatbot:moderation.underReview.description"} values={entityType} />
  ) : (
    <Trans i18nKey={"lorebook:moderation.underReview.description"} values={entityType} />
  );
};

const getContactLinkTransComponent = ({ applicationId }: { applicationId: string }) => {
  return {
    contactLink: (
      <LocalizedLink
        className="text-blue-600"
        to={`${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs}/support`}
        target="_blank"
        aria-label="contact-us"
      />
    ),
  };
};

export default function ModerationStatus({
  applicationId,
  moderationReasonCodes = [],
  actionTakenDescription,
  reasonForModeration = [],
  moderationStatus,
  reportsType = [],
  type,
  category = "chatbot",
}: IModerationStatusProps) {
  const { getTranslatedModerationReason } = useTranslatedModerationReason();
  // let moderationStatus: CHARACTER_MODERATION_STATUS | null = (moderation_status as CHARACTER_MODERATION_STATUS) ?? null;
  // checking the type because there are characters which still have empty object in reportsType
  const isReportTypeUser = Array.isArray(reportsType) && reportsType?.includes("User");

  if (
    (moderationStatus && !Object.values(CHARACTER_MODERATION_STATUS).includes(moderationStatus as CHARACTER_MODERATION_STATUS)) ||
    moderationStatus === CHARACTER_MODERATION_STATUS.CLOSED
  ) {
    moderationStatus = null;
  }

  const { moderationBorder, moderationColor } = TextStyles.moderationStyles(moderationStatus);

  const translatedReasons = useMemo<string[]>(() => {
    const codes: string[] = moderationReasonCodes;
    const englishReasons: string[] = reasonForModeration;
    if (codes.length > 0) {
      return codes.map((code, index) => getTranslatedModerationReason(code, englishReasons[index]));
    }
    return englishReasons;
  }, [moderationReasonCodes, reasonForModeration, getTranslatedModerationReason]);

  if (!moderationStatus) {
    return null;
  }

  return (
    <Grid className="flex justify-between flex-col mb-4">
      <Container className={`h-fit w-full flex bg-[#353535] rounded-[10px] p-[10px] border-0 border-b-5 border-solid ` + moderationBorder}>
        <Row>
          <Text type={moderationColor}>{renderModerationTitle({ category, characterType: type, isReportTypeUser, moderationStatus })}</Text>
        </Row>
        <Col className="mt-2">
          <Text n type="steel_blue">
            {renderModerationDescription({ category, characterType: type, isReportTypeUser, moderationStatus })}
          </Text>
          <Spacer y={1} />
          {moderationStatus === CHARACTER_MODERATION_STATUS.FIX_REQUIRED && (
            <>
              {translatedReasons.length > 0 && (
                <div>
                  <Text n type={"steel_blue"}>
                    {category === "chatbot" ? (
                      <Trans i18nKey={"chatbot:moderation.fixRequired.reasons"} />
                    ) : (
                      <Trans i18nKey={"lorebook:moderation.fixRequired.reasons"} />
                    )}
                  </Text>
                  <ul>
                    {translatedReasons.map((reason, index) => {
                      return (
                        <li key={index} className="list-disc ml-[18px]">
                          <Text n type={"steel_blue"}>
                            {reason}
                          </Text>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <div>
                {actionTakenDescription ? (
                  <Col>
                    <Spacer y={2} />
                    <Text n type={"steel_blue"}>
                      {category === "chatbot" ? (
                        <Trans i18nKey={"chatbot:moderation.fixRequired.modNote"} />
                      ) : (
                        <Trans i18nKey={"lorebook:moderation.fixRequired.modNote"} />
                      )}
                    </Text>
                    <Text n type={"steel_blue"}>
                      {actionTakenDescription}
                    </Text>
                  </Col>
                ) : null}
                <Spacer y={2} />
                <Text n type={"steel_blue"}>
                  {category === "chatbot" ? (
                    <Trans i18nKey={"chatbot:moderation.fixRequired.contactSupport"} components={getContactLinkTransComponent({ applicationId })} />
                  ) : (
                    <Trans i18nKey={"lorebook:moderation.fixRequired.contactSupport"} components={getContactLinkTransComponent({ applicationId })} />
                  )}
                </Text>
              </div>
            </>
          )}
        </Col>
      </Container>
    </Grid>
  );
}
